import React, { useEffect, useState, useMemo, Fragment, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getBEMClasses } from 'apex-web/lib/helpers/cssClassesHelper';
import withAuthentication from 'apex-web/lib/hocs/withAuthentication';
import APIcon from 'apex-web/lib/components/common/APIcon';
import Spinner from 'apex-web/lib/components/common/Spinner/Spinner';
import PageFooterComponent from 'apex-web/lib/components/PageFooter/PageFooterComponent';
import { ConnectedTotalTokens } from 'apex-web/lib/components/DigitalAssets/TotalTokensCount';
import 'apex-web/lib/components/DigitalAssets/DigitalAssetsPage.css';

const LIST_VIEW_KEY = 'digitalAssetsListView';
/** Narrow layouts default to cards unless the user chose table on mobile (stored separately from desktop). */
const LIST_VIEW_KEY_MOBILE = 'digitalAssetsListViewMobile';
const classes = getBEMClasses('digital-assets');

function isNarrowDigitalAssetsViewport() {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.matchMedia('(max-width: 720px)').matches;
  } catch (e) {
    return false;
  }
}

/** Resolves at build time like other `public/` assets; avoids path-helper import issues */
const DA_CARD_HEADER_BG_PATH = '/local/landing-page/images/assetBG.jpg';
const DA_HEADER_ART_BG = `${process.env.PUBLIC_URL || ''}${DA_CARD_HEADER_BG_PATH}`;
const DA_HEADER_ART_STYLE = {
  backgroundColor: '#040910',
  backgroundImage: `linear-gradient(180deg, rgba(2, 6, 16, 0.15) 0%, rgba(2, 10, 24, 0.65) 100%), url(${JSON.stringify(DA_HEADER_ART_BG)})`,
  backgroundSize: 'cover, cover',
  backgroundPosition: 'center top, center top',
  backgroundRepeat: 'no-repeat, no-repeat',
};
const LEGACY_NO_IMAGE = 'http://offering-profiles-samples.surge.sh/noimagefound.png';
/** GetProductAttributes stores Key/Value; keys may not always match this exact name */
const IMAGE_URL_KEYS = [
  'ProductImageURL',
  'ProductImageUrl',
  'productImageURL',
  'productImageUrl',
  'BannerImageURL',
  'ImageURL',
  'imageUrl'
];

function getFirstImageUrlFromProduct(data) {
  if (!data) {
    return '';
  }
  for (const key of IMAGE_URL_KEYS) {
    if (data[key] != null) {
      const t = String(data[key]).trim();
      if (t) {
        return t;
      }
    }
  }
  return '';
}

/** Returns a usable image URL, or `null` to use the built-in “token explorer” style header art */
function resolveHeaderImageUrl(url) {
  if (url == null) {
    return null;
  }
  const s = String(url).trim();
  if (!s) {
    return null;
  }
  const low = s.toLowerCase();
  if (
    low === 'null' ||
    low === 'undefined' ||
    low === 'n/a' ||
    low === 'about:blank' ||
    low === '#'
  ) {
    return null;
  }
  if (s === LEGACY_NO_IMAGE) {
    return null;
  }
  return s;
}

function getCardThemeIndex(productId) {
  const n = parseInt(productId, 10);
  return Number.isFinite(n) ? Math.abs(n) % 4 : 0;
}

/** two-letter (or one) label for product avatar fallback — shared by cards and table */
function getProductAvatarLabel(data) {
  if (!data) {
    return '•';
  }
  const p = (data.Product && String(data.Product).trim()) || '';
  if (p.length >= 2) {
    return p.slice(0, 2).toUpperCase();
  }
  if (data.ProductFullName) {
    const t = String(data.ProductFullName)
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(w => w[0])
      .join('');
    return (t.slice(0, 2) || '•').toUpperCase();
  }
  return '•';
}

function readListView() {
  try {
    if (typeof window === 'undefined') {
      return 'cards';
    }
    const key = isNarrowDigitalAssetsViewport()
      ? LIST_VIEW_KEY_MOBILE
      : LIST_VIEW_KEY;
    const v = window.localStorage.getItem(key);
    return v === 'table' ? 'table' : 'cards';
  } catch (e) {
    return 'cards';
  }
}

function ViewIconCards() {
  return (
    <svg
      className="digital-assets__view-icon-svg"
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="currentColor"
      aria-hidden
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function ViewIconTable() {
  return (
    <svg
      className="digital-assets__view-icon-svg"
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="currentColor"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="3" rx="0.5" />
      <rect x="3" y="10" width="18" height="3" rx="0.5" />
      <rect x="3" y="16" width="18" height="3" rx="0.5" />
    </svg>
  );
}

function TableProductThumb({ data }) {
  const rawUrl = getFirstImageUrlFromProduct(data);
  const imageUrl = useMemo(() => resolveHeaderImageUrl(rawUrl), [rawUrl]);
  const [imageFailed, setImageFailed] = useState(false);
  const hasAvatar = imageUrl != null && !imageFailed;
  const label = getProductAvatarLabel(data);
  const theme = getCardThemeIndex(data.ProductId);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl, data.ProductId]);

  return (
    <div className="digital-assets__table-product-thumb" aria-hidden data-theme={String(theme)}>
      {hasAvatar ? (
        <img
          className="digital-assets__table-product-thumb-img"
          src={imageUrl}
          alt=""
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="digital-assets__table-product-thumb-fallback">
          {label}
        </span>
      )}
    </div>
  );
}

const DigitalAssetsTable = ({ items }) => {
  return (
    <div className="digital-assets__table-outer">
      <div className="digital-assets__table-wrap" role="region" aria-label="Digital assets table">
        <table className="digital-assets__table">
          <caption className="digital-assets__table-caption">Ongoing digital asset offerings</caption>
          <colgroup>
            <col className="digital-assets__table-col--product" />
            <col className="digital-assets__table-col--name" />
            <col className="digital-assets__table-col--desc" />
            <col className="digital-assets__table-col--tokens" />
            <col className="digital-assets__table-col--action" />
          </colgroup>
          <thead>
            <tr>
              <th className="digital-assets__table-th digital-assets__table-th--product" scope="col">
                Product
              </th>
              <th className="digital-assets__table-th digital-assets__table-th--name" scope="col">
                Name
              </th>
              <th className="digital-assets__table-th digital-assets__table-th--desc" scope="col">
                Description
              </th>
              <th
                className="digital-assets__table-th digital-assets__table-th--tokens"
                scope="col"
              >
                Tokens offered
              </th>
              <th className="digital-assets__table-th digital-assets__table-th--action" scope="col">
                <span className="digital-assets__table-sr">Action</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map(row => {
              const desc = row.ProductDescription && String(row.ProductDescription).trim();
              return (
                <tr className="digital-assets__table-tr" key={row.ProductId}>
                  <td className="digital-assets__table-td">
                    <div className="digital-assets__table-product">
                      <TableProductThumb data={row} />
                      <div className="digital-assets__table-product-text">
                        <span className="digital-assets__table-product-symbol">{row.Product || '—'}</span>
                        <span className="digital-assets__table-product-id">#{row.ProductId}</span>
                      </div>
                    </div>
                  </td>
                  <td className="digital-assets__table-td digital-assets__table-td--name" title={row.ProductFullName || ''}>
                    <span className="digital-assets__table-name-text">{row.ProductFullName || '—'}</span>
                  </td>
                  <td
                    className="digital-assets__table-td digital-assets__table-td--desc"
                    title={desc || undefined}
                  >
                    {desc ? (
                      <span className="digital-assets__table-desc-text">{desc}</span>
                    ) : (
                      <span className="digital-assets__table-td--empty" aria-label="No description">
                        —
                      </span>
                    )}
                  </td>
                  <td className="digital-assets__table-td digital-assets__table-td--num">
                    <span className="digital-assets__table-tokens">
                      <ConnectedTotalTokens ProductId={row.ProductId} />
                    </span>
                  </td>
                  <td className="digital-assets__table-td digital-assets__table-td--action">
                    <Link
                      className="digital-assets__table-link"
                      to={`/digital-assets/${row.ProductId}`}
                    >
                      <span className="digital-assets__table-link__text">View</span>
                      <span className="digital-assets__table-link__arrow" aria-hidden>
                        <APIcon name="arrowNext" />
                      </span>
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DigitalAssetsPage = ({ products, digitalAssets, getProductAttributes }) => {
  const [listView, setListView] = useState(readListView);

  const setListViewAndPersist = useCallback(next => {
    setListView(next);
    try {
      if (typeof window !== 'undefined') {
        const key = isNarrowDigitalAssetsViewport()
          ? LIST_VIEW_KEY_MOBILE
          : LIST_VIEW_KEY;
        window.localStorage.setItem(key, next);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    getProductAttributes(products);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 720px)');
    const onChange = () => setListView(readListView());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return (
    <Fragment>
      <div className={classes('accent-bar')} />
      <div className={classes('container')}>
        {!products.length ? (
          <Spinner />
        ) : digitalAssets.length ? (
          <Fragment>
            <div className={classes('list-toolbar')}>
              <div className={classes('container-header')}>Ongoing Offerings</div>
              <div
                className="digital-assets__view-toggle"
                role="group"
                aria-label="List layout"
              >
                <button
                  type="button"
                  className={`digital-assets__view-btn${listView === 'cards' ? ' digital-assets__view-btn--active' : ''}`}
                  aria-pressed={listView === 'cards'}
                  aria-label="Card view"
                  title="Card view"
                  onClick={() => setListViewAndPersist('cards')}
                >
                  <ViewIconCards />
                </button>
                <button
                  type="button"
                  className={`digital-assets__view-btn${listView === 'table' ? ' digital-assets__view-btn--active' : ''}`}
                  aria-pressed={listView === 'table'}
                  aria-label="Table view"
                  title="Table view"
                  onClick={() => setListViewAndPersist('table')}
                >
                  <ViewIconTable />
                </button>
              </div>
            </div>
            <div className={classes('col', ['lg'])}>
              {listView === 'cards' ? (
                <div className={classes('container-with-shadow')}>
                  {digitalAssets.map(card => (
                    <Card data={card} key={card.ProductId} />
                  ))}
                </div>
              ) : (
                <div className={classes('table-panel')}>
                  <DigitalAssetsTable items={digitalAssets} />
                </div>
              )}
            </div>
          </Fragment>
        ) : (
          <div className={classes('container-notfound')}>No Digital Assets could be found</div>
        )}
      </div>
      <div className={classes('footer')}>
        <PageFooterComponent />
      </div>
    </Fragment>
  );
};

const Card = ({ data }) => {
  const rawUrl = getFirstImageUrlFromProduct(data);
  const imageUrl = useMemo(() => resolveHeaderImageUrl(rawUrl), [rawUrl]);
  const [imageFailed, setImageFailed] = useState(false);
  const hasAvatarImage = imageUrl != null && !imageFailed;
  const titlePrimary = (data.Product && String(data.Product).trim()) || data.ProductFullName;
  const hasSubtitle =
    data.Product &&
    data.ProductFullName &&
    String(data.Product).trim() &&
    String(data.Product).trim() !== String(data.ProductFullName).trim();
  const avatarFallback = getProductAvatarLabel(data);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl, data.ProductId]);

  return (
    <div className={classes('card')}>
      <div className={classes('header-div')}>
        <div className="digital-assets__header-art" style={DA_HEADER_ART_STYLE} aria-hidden />
        <div className="digital-assets__header-foreground">
          <div className="digital-assets__header-identity">
            <div className="digital-assets__token-avatar" aria-hidden>
              {hasAvatarImage ? (
                <img
                  className="digital-assets__token-avatar-img"
                  src={imageUrl}
                  alt=""
                  onError={() => setImageFailed(true)}
                />
              ) : (
                <span className="digital-assets__token-avatar-fallback">
                  {avatarFallback}
                </span>
              )}
            </div>
            <div className="digital-assets__header-text">
              <div className="digital-assets__header-line1">
                {titlePrimary}
              </div>
              {hasSubtitle && (
                <div className="digital-assets__header-line2">
                  {data.ProductFullName}
                </div>
              )}
            </div>
          </div>
          <div className="digital-assets__header-rank" title={`Product ${data.ProductId}`}>
            #{data.ProductId}
          </div>
        </div>
      </div>
      <div className={classes('card-lower')}>
        <div className={classes('card-body-frost')}>
          <div className={classes('card-description')}>{data.ProductDescription}</div>
          <div className={classes('details')}>
            <div className={classes('left-detail')}>
              <span className={classes('details-label')}>Amount of tokens offered:</span>
              <span className={classes('details-value')}>
                <ConnectedTotalTokens ProductId={data.ProductId} />
              </span>
            </div>
          </div>
        </div>
        <div className={classes('card-actions')}>
          <div className={classes('view-more')}>
            <Link to={`/digital-assets/${data.ProductId}`}>
              View More &nbsp;&nbsp;
              <APIcon name="arrowNext" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAuthentication(DigitalAssetsPage);
